// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useState, useMemo } from 'react';
import { useDebounce } from './use-debounce';

type FieldNames<T> = (keyof T)[];

interface UseTableTextSearchFilterArgs<T> {
  data: T[];
  searchFields: FieldNames<T>;
  sortField?: keyof T;
  sortDescending?: boolean;
  debounceMs?: number;
}

export function useTableTextSearchFilter<T>({
  data,
  searchFields,
  sortField,
  sortDescending = true,
  debounceMs = 300,
}: UseTableTextSearchFilterArgs<T>) {
  const [textSearch, setTextSearch] = useState('');
  const debounceValue = useDebounce(textSearch, debounceMs);

  const handleTextSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setTextSearch(event.target.value);

  const filteredData = useMemo(() => {
    const searchTerm = debounceValue.toLowerCase().trim();
    let filteredList = searchTerm
      ? data.filter(item =>
          searchFields.some(field =>
            item[field]?.toString().toLowerCase().includes(searchTerm),
          ),
        )
      : data;

    if (sortField) {
      filteredList = [...filteredList].sort((a, b) => {
        const aVal = a[sortField]?.toString() ?? '';
        const bVal = b[sortField]?.toString() ?? '';
        return sortDescending ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      });
    }

    return filteredList;
  }, [data, debounceValue, searchFields, sortField, sortDescending]);

  return {
    filteredData,
    textSearch,
    setTextSearch,
    handleTextSearch,
  };
}
